// The Browse tab draws the whole visible catalogue as one list with its facets over it,
// out of the same shop rows every other buying surface draws.

import { charged, pounds } from "./cart";
import type { BakedRow } from "./catalogue";
import {
  coveredLocations,
  coversLocation,
  grantsDr,
  type BodyLocation,
} from "./coverage";
import { qualifiedName, type QualityTable, type Stall } from "./quality";
import { shopList, type ShopSurface } from "./shop";
import { damagesOf, modesOf, DAMAGES, MODES, type Damage, type Mode } from "./usages";

export const TYPES = ["all", "weapon", "shield", "armour", "gear"] as const;
export type TypeChip = (typeof TYPES)[number];
export type RowType = Exclude<TypeChip, "all">;

export const SORTS = ["price", "weight", "name"] as const;
export type SortKey = (typeof SORTS)[number];

/** The values the query string carries, and nothing the cart store ever sees. A sub-filter
 *  belongs to one type chip and is read only while that chip is the one selected. */
export interface Facets {
  text: string;
  type: TypeChip;
  shop: string | null;
  mode: Mode | null;
  damage: Damage | null;
  location: string | null;
  sort: SortKey;
  descending: boolean;
}

export const NO_FACETS: Facets = {
  text: "",
  type: "all",
  shop: null,
  mode: null,
  damage: null,
  location: null,
  sort: "price",
  descending: false,
};

const TYPE_LABELS: Record<TypeChip, string> = {
  all: "All",
  weapon: "Weapon",
  shield: "Shield",
  armour: "Armour",
  gear: "Gear",
};

const SORT_LABELS: Record<SortKey, string> = {
  price: "Price",
  weight: "Weight",
  name: "Name",
};

const MODE_LABELS: Record<Mode, string> = {
  melee: "Melee",
  ranged: "Ranged",
};

const DAMAGE_LABELS: Record<Damage, string> = {
  crushing: "Crushing",
  cutting: "Cutting",
  impaling: "Impaling",
  piercing: "Piercing",
};

/** Reordering these four puts a main-gauche under Armour, where no player looks for it. */
export function classify(row: BakedRow): RowType {
  const tags = row["tags"];
  if (Array.isArray(tags) && tags.includes("Shield")) return "shield";
  const weapons = row["weapons"];
  if (Array.isArray(weapons) && weapons.length > 0) return "weapon";
  if (grantsDr(row)) return "armour";
  return "gear";
}

/** Every name the row answers to, the library description among them, so a curated rename
 *  hides no row from a player typing the stock GURPS word. The GCS notes are not a name and
 *  are not searched. */
function names(stall: Stall, table?: QualityTable): string {
  return [
    stall.stock.description,
    stall.stock.stock_description ?? "",
    qualifiedName(stall.stock, table),
    ...stall.tiers.map((tier) => qualifiedName(tier.row, table)),
  ]
    .join(" ")
    .toLowerCase();
}

/** Price and weight sort on the numbers the row prints, which on a 320-page grimoire are
 *  320 pages of both. */
function key(
  stall: Stall,
  sort: SortKey,
  table?: QualityTable,
  counts?: Counts,
): number | string {
  const held = counts?.get(stall.item) ?? 1;
  if (sort === "price") return charged(stall.stock, held);
  if (sort === "weight") return pounds(stall.stock, held);
  return qualifiedName(stall.stock, table).toLowerCase();
}

function ordered(
  stalls: Stall[],
  facets: Facets,
  table?: QualityTable,
  counts?: Counts,
): Stall[] {
  const sorted = [...stalls].sort((left, right) => {
    const a = key(left, facets.sort, table, counts);
    const b = key(right, facets.sort, table, counts);
    if (a !== b) return a < b ? -1 : 1;
    return key(left, "name", table) < key(right, "name", table) ? -1 : 1;
  });
  return facets.descending ? sorted.reverse() : sorted;
}

export type ShopsOf = (item: string) => string[];
type Counts = ReadonlyMap<string, number>;

/** The sub-filters this type chip reads. A location filter left over from Armour cannot
 *  reach a Weapon view and empty it, whatever the query string says. */
function subFilters(facets: Facets): {
  mode: Mode | null;
  damage: Damage | null;
  location: string | null;
} {
  return {
    mode: facets.type === "weapon" ? (facets.mode ?? null) : null,
    damage: facets.type === "weapon" ? (facets.damage ?? null) : null,
    location: facets.type === "armour" ? (facets.location ?? null) : null,
  };
}

/** Text AND type AND shop AND each sub-filter narrow independently, then the sort orders
 *  what survives. */
export function surviving(
  stalls: Stall[],
  facets: Facets,
  shopsOf: ShopsOf,
  table?: QualityTable,
  counts?: Counts,
): Stall[] {
  const wanted = facets.text.trim().toLowerCase();
  const { mode, damage, location } = subFilters(facets);
  const kept = stalls.filter(
    (stall) =>
      (wanted === "" || names(stall, table).includes(wanted)) &&
      (facets.type === "all" || classify(stall.stock) === facets.type) &&
      (facets.shop === null || shopsOf(stall.item).includes(facets.shop)) &&
      (mode === null || modesOf(stall.stock).has(mode)) &&
      (damage === null || damagesOf(stall.stock).has(damage)) &&
      (location === null || coversLocation(stall.stock, location)),
  );
  return ordered(kept, facets, table, counts);
}

/** The hit locations this character's armour rows cover, which is the chip set the Armour
 *  chip raises. A lizardman's tail draws no chip while nothing armours it. */
export function armouredLocations(
  stalls: Stall[],
  locations: readonly BodyLocation[],
): BodyLocation[] {
  return coveredLocations(
    stalls.filter((stall) => classify(stall.stock) === "armour"),
    locations,
  );
}

/** The declared shops holding at least one row this character can see. */
export function offeredShops(stalls: Stall[], declared: string[], shopsOf: ShopsOf): string[] {
  const stocked = new Set(stalls.flatMap((stall) => shopsOf(stall.item)));
  return declared.filter((shop) => stocked.has(shop));
}

function member<T extends readonly string[]>(all: T, value: string | null): value is T[number] {
  return value !== null && (all as readonly string[]).includes(value);
}

/** A query naming a shop this character has no row in falls back to no shop filter, and a
 *  sub-filter whose type chip is not the one selected falls back to no sub-filter. */
export function readFacets(
  search: string,
  offered: string[],
  armoured: string[] = [],
): Facets {
  const query = new URLSearchParams(search);
  const named = query.get("type");
  const type = member(TYPES, named) ? named : "all";
  const shop = query.get("shop");
  const mode = query.get("mode");
  const damage = query.get("damage");
  const location = query.get("location");
  const written = query.get("sort") ?? "";
  const descending = written.startsWith("-");
  const sort = descending ? written.slice(1) : written;
  return {
    text: query.get("q") ?? "",
    type,
    shop: shop !== null && offered.includes(shop) ? shop : null,
    mode: type === "weapon" && member(MODES, mode) ? mode : null,
    damage: type === "weapon" && member(DAMAGES, damage) ? damage : null,
    location: type === "armour" && location !== null && armoured.includes(location) ? location : null,
    sort: member(SORTS, sort) ? sort : NO_FACETS.sort,
    descending: member(SORTS, sort) ? descending : false,
  };
}

export function writeFacets(facets: Facets): string {
  const query = new URLSearchParams();
  const { mode, damage, location } = subFilters(facets);
  if (facets.text !== "") query.set("q", facets.text);
  if (facets.type !== "all") query.set("type", facets.type);
  if (facets.shop !== null) query.set("shop", facets.shop);
  if (mode !== null) query.set("mode", mode);
  if (damage !== null) query.set("damage", damage);
  if (location !== null) query.set("location", location);
  if (facets.sort !== NO_FACETS.sort || facets.descending) {
    query.set("sort", `${facets.descending ? "-" : ""}${facets.sort}`);
  }
  const written = query.toString();
  return written === "" ? "" : `?${written}`;
}

export interface BrowseSurface extends ShopSurface {
  stalls: Stall[];
  /** Every shop the curation declares, which is what the two-shop threshold counts. */
  declared: string[];
  shopsOf: ShopsOf;
  facets: Facets;
  onFacets(facets: Facets): void;
}

const CLEARED = { mode: null, damage: null, location: null } as const;

export function browsePanel(surface: BrowseSurface): HTMLElement {
  const root = document.createElement("section");
  root.className = "browse";
  let facets = surface.facets;

  const search = document.createElement("input");
  search.className = "browse-search";
  search.type = "search";
  search.placeholder = "Search";
  search.value = facets.text;
  search.addEventListener("input", () => change({ ...facets, text: search.value }));

  const types = document.createElement("div");
  types.className = "browse-chips browse-types";
  const subs = document.createElement("div");
  subs.className = "browse-chips browse-subs";
  const shops = document.createElement("div");
  shops.className = "browse-chips browse-shops";
  const sorts = document.createElement("div");
  sorts.className = "browse-chips browse-sorts";
  const count = document.createElement("p");
  count.className = "browse-count";
  const list = document.createElement("div");
  list.className = "browse-list";

  const offered = offeredShops(surface.stalls, surface.declared, surface.shopsOf);
  const armoured = armouredLocations(surface.stalls, surface.locations ?? []);

  function change(next: Facets): void {
    facets = next;
    surface.onFacets(next);
    paint();
  }

  function chip(
    className: string,
    label: string,
    field: string,
    value: string,
    chosen: boolean,
    tapped: () => void,
  ): HTMLElement {
    const button = document.createElement("button");
    button.className = chosen ? `${className} browse-chip-chosen` : className;
    button.dataset[field] = value;
    button.textContent = label;
    button.addEventListener("click", tapped);
    return button;
  }

  /** The row a type chip raises under itself, and only while that chip is the one selected,
   *  so the phone grows no permanent third chip row. */
  function subChips(): HTMLElement[] {
    if (facets.type === "weapon") {
      return [
        ...MODES.map((mode) =>
          chip("browse-chip", MODE_LABELS[mode], "mode", mode, facets.mode === mode, () =>
            change({ ...facets, mode: facets.mode === mode ? null : mode }),
          ),
        ),
        ...DAMAGES.map((damage) =>
          chip("browse-chip", DAMAGE_LABELS[damage], "damage", damage, facets.damage === damage, () =>
            change({ ...facets, damage: facets.damage === damage ? null : damage }),
          ),
        ),
      ];
    }
    if (facets.type === "armour") {
      return armoured.map((where) =>
        chip("browse-chip", where.name, "location", where.id, facets.location === where.id, () =>
          change({ ...facets, location: facets.location === where.id ? null : where.id }),
        ),
      );
    }
    return [];
  }

  function paint(): void {
    types.replaceChildren(
      ...TYPES.map((type) =>
        chip("browse-chip", TYPE_LABELS[type], "type", type, facets.type === type, () =>
          change(facets.type === type ? { ...facets, type } : { ...facets, type, ...CLEARED }),
        ),
      ),
    );

    subs.replaceChildren(...subChips());

    if (surface.declared.length >= 2) {
      shops.replaceChildren(
        chip("browse-chip", "All shops", "shop", "", facets.shop === null, () =>
          change({ ...facets, shop: null }),
        ),
        ...offered.map((shop) =>
          chip("browse-chip", shop, "shop", shop, facets.shop === shop, () =>
            change({ ...facets, shop }),
          ),
        ),
      );
    } else {
      shops.replaceChildren();
    }

    sorts.replaceChildren(
      ...SORTS.map((sort) => {
        const chosen = facets.sort === sort;
        const label = chosen
          ? `${SORT_LABELS[sort]} ${facets.descending ? "v" : "^"}`
          : SORT_LABELS[sort];
        return chip("browse-sort", label, "sort", sort, chosen, () =>
          change({ ...facets, sort, descending: chosen ? !facets.descending : false }),
        );
      }),
    );

    const shown = surviving(
      surface.stalls,
      facets,
      surface.shopsOf,
      surface.table,
      surface.counts,
    );
    count.textContent = `${shown.length} of ${surface.stalls.length}`;
    list.replaceChildren(shopList(shown, surface));
  }

  paint();
  root.append(search, types, subs, shops, sorts, count, list);
  return root;
}
